import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie, Shield, BarChart3, X, ChevronDown, ChevronUp } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'grc_cookie_consent';

// Cookie categories
const COOKIE_CATEGORIES = {
  essential: {
    id: 'essential',
    name: 'Essential Cookies',
    description: 'These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you such as setting your privacy preferences, logging in, or filling in forms.',
    icon: Shield,
    required: true
  },
  session: {
    id: 'session',
    name: 'Session Cookies',
    description: 'Session cookies help us remember your preferences and keep you logged in during your visit. These cookies are deleted when you close your browser.',
    icon: Cookie,
    required: true
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing Cookies',
    description: 'These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.',
    icon: BarChart3,
    required: false
  }
};

const CookieConsent = ({ onConsentChange }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState({
    essential: true,
    session: true,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already given consent
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        setIsVisible(false);
        if (onConsentChange) onConsentChange(parsed);
      } catch (e) {
        setIsVisible(true);
      }
    } else {
      setIsVisible(true);
    }
  }, [onConsentChange]);

  const saveConsent = (newConsent) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newConsent));
    setConsent(newConsent);
    setIsVisible(false);
    if (onConsentChange) onConsentChange(newConsent);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      session: true,
      marketing: true
    };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      session: true,
      marketing: false
    };
    saveConsent(onlyEssential);
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
  };

  const toggleCategory = (categoryId) => {
    if (COOKIE_CATEGORIES[categoryId].required) return;
    setConsent(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
      <Card className="w-full max-w-2xl bg-white dark:bg-gray-900 shadow-2xl border-2 border-primary/20 pointer-events-auto animate-in slide-in-from-bottom-5 duration-300">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Cookie Preferences</h3>
                <p className="text-sm text-muted-foreground">Manage your cookie settings</p>
              </div>
            </div>
          </div>

          {/* Main description */}
          <p className="text-sm text-muted-foreground mb-4">
            We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
            By clicking "Accept All", you consent to our use of cookies.
          </p>

          {/* Cookie categories toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors mb-4"
          >
            {showDetails ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide cookie details
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show cookie details
              </>
            )}
          </button>

          {/* Cookie categories details */}
          {showDetails && (
            <div className="space-y-3 mb-6 p-4 bg-muted/30 rounded-lg">
              {Object.values(COOKIE_CATEGORIES).map((category) => {
                const Icon = category.icon;
                return (
                  <div key={category.id} className="flex items-start gap-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-border">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor={category.id} className="font-medium text-foreground">
                          {category.name}
                          {category.required && (
                            <span className="ml-2 text-xs text-muted-foreground">(Required)</span>
                          )}
                        </Label>
                        <Switch
                          id={category.id}
                          checked={consent[category.id]}
                          onCheckedChange={() => toggleCategory(category.id)}
                          disabled={category.required}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{category.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              className="flex-1 sm:flex-none"
              data-testid="cookie-reject-all"
            >
              Reject All
            </Button>
            {showDetails && (
              <Button
                variant="outline"
                onClick={handleSavePreferences}
                className="flex-1 sm:flex-none"
                data-testid="cookie-save-preferences"
              >
                Save Preferences
              </Button>
            )}
            <Button
              onClick={handleAcceptAll}
              className="flex-1 sm:flex-none"
              data-testid="cookie-accept-all"
            >
              Accept All
            </Button>
          </div>

          {/* Privacy link */}
          <p className="text-xs text-muted-foreground mt-4 text-center">
            Learn more about how we use cookies in our{' '}
            <a
              href="https://www.beskarit.com/Privacy-Policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default CookieConsent;
